// utils/format.js — helpers de formatage i18n (dates, nombres, monnaie).
// ---------------------------------------------------------------------------
// Centralise les Intl.* pour ne plus avoir de "toLocaleDateString('fr-FR')"
// hardcodé partout dans le code. Mapping lang (i18next 2-letter) → BCP-47
// locale via LOCALE_MAP.
//
// Choix locale par langue :
//   fr → fr-FR   (séparateur , et espace)
//   en → en-GB   (date 15 Mar 2026 ; 1,250.50)
//   es → es-ES   (15 mar 2026 ; 1.250,50)
//   it → it-IT   (15 mar 2026 ; 1.250,50)
//
// Toutes les fonctions sont défensives : un input invalide → string vide
// (ou fallback raisonnable). Jamais d'exception remontée à l'UI.
//
// Pluralisation : gérée côté i18next via le suffixe _one/_other (pas ici).
// ---------------------------------------------------------------------------

export const LOCALE_MAP = {
  fr: 'fr-FR',
  en: 'en-GB',
  es: 'es-ES',
  it: 'it-IT',
};

/** Renvoie la locale BCP-47 pour une langue i18next, fr-FR par défaut. */
export function getLocale(lang) {
  return LOCALE_MAP[lang] || 'fr-FR';
}

/**
 * Formate une date ISO en string locale.
 *   formatDate('2026-05-15', 'fr')                  → "15 mai 2026"
 *   formatDate('2026-05-15', 'en')                  → "15 May 2026"
 *   formatDate('2026-05-15', 'en', { dateStyle: 'short' }) → "15/05/2026"
 *
 * Options Intl.DateTimeFormat par défaut :
 *   { day: '2-digit', month: 'long', year: 'numeric' }
 */
export function formatDate(isoOrDate, lang, options) {
  if (!isoOrDate) return '';
  try {
    const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return '';
    const opts = options || { day: '2-digit', month: 'long', year: 'numeric' };
    return new Intl.DateTimeFormat(getLocale(lang), opts).format(d);
  } catch {
    return '';
  }
}

/**
 * Formate "mois année" sans le jour (sélecteur de mois).
 *   formatMonthYear('2026-04', 'fr') → "avril 2026"
 *   formatMonthYear('2026-04', 'en') → "April 2026"
 *
 * Accepte 'YYYY-MM' OU une Date OU un ISO complet.
 */
export function formatMonthYear(input, lang) {
  if (!input) return '';
  try {
    let d;
    if (typeof input === 'string' && /^\d{4}-\d{2}$/.test(input)) {
      const [y, m] = input.split('-').map(Number);
      d = new Date(y, m - 1, 1);
    } else {
      d = input instanceof Date ? input : new Date(input);
    }
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(getLocale(lang), {
      month: 'long',
      year: 'numeric',
    }).format(d);
  } catch {
    return '';
  }
}

/**
 * Formate un mois court (3 lettres, ex: "mai", "May").
 *   formatShortMonth(new Date(2026, 4, 1), 'fr') → "mai"
 *   formatShortMonth(new Date(2026, 4, 1), 'en') → "May"
 */
export function formatShortMonth(input, lang) {
  if (!input) return '';
  try {
    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(getLocale(lang), { month: 'short' }).format(d);
  } catch {
    return '';
  }
}

/**
 * Formate un nombre selon la locale.
 *   formatNumber(1250.5, 'fr') → "1 250,5"
 *   formatNumber(1250.5, 'en') → "1,250.5"
 */
export function formatNumber(n, lang, options) {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  try {
    return new Intl.NumberFormat(getLocale(lang), options).format(n);
  } catch {
    return String(n);
  }
}

/**
 * Formate un montant en devise.
 *   formatCurrency(89, 'fr', 'EUR') → "89,00 €"
 *   formatCurrency(89, 'en', 'EUR') → "€89.00"
 *   formatCurrency(1250.5, 'fr', 'EUR') → "1 250,50 €"
 */
export function formatCurrency(amount, lang, currency = 'EUR') {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '';
  try {
    return new Intl.NumberFormat(getLocale(lang), {
      style: 'currency',
      currency: (currency || 'EUR').toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

/**
 * Formate un PRIX DE PLAN (grille tarifaire) selon la locale.
 *
 * Différence avec formatCurrency : les décimales sont adaptatives.
 *   - prix entier  → 0 décimale  (89 → "89 €", pas "89,00 €")
 *   - prix décimal → 2 décimales (9.99 → "9,99 €", pas "9.99€")
 *
 *   formatPrice(9.99, 'fr') → "9,99 €"
 *   formatPrice(9.99, 'en') → "€9.99"
 *   formatPrice(9.99, 'es') → "9,99 €"
 *   formatPrice(89,   'fr') → "89 €"
 *
 * Ajouté par le chantier « palier Découverte » (30/07/2026) : jusque-là les prix
 * étaient des entiers rendus en `{price}€`, ce qui aurait affiché « 9.99€ ».
 * Le symbole et sa position sont gérés par Intl — ne PAS concaténer un « € »
 * à la main autour de cette fonction.
 */
export function formatPrice(price, lang, currency = 'EUR') {
  if (price === null || price === undefined || Number.isNaN(Number(price))) return '';
  const n = Number(price);
  try {
    return new Intl.NumberFormat(getLocale(lang), {
      style: 'currency',
      currency: (currency || 'EUR').toUpperCase(),
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n} €`;
  }
}

/**
 * Même formatage que formatPrice, mais découpé en parties pour les designs qui
 * rendent le symbole monétaire dans un span plus petit que le nombre
 * (grille tarifaire : nombre en 42px, symbole en 22px).
 *
 *   formatPriceParts(9.99, 'fr') → { value: '9,99', currency: '€', currencyFirst: false }
 *   formatPriceParts(9.99, 'en') → { value: '9.99', currency: '€', currencyFirst: true }
 *   formatPriceParts(89,   'fr') → { value: '89',   currency: '€', currencyFirst: false }
 *
 * `currencyFirst` dit si le symbole précède le nombre dans cette locale — ne
 * jamais présumer de la position, c'est Intl qui décide.
 */
export function formatPriceParts(price, lang, currency = 'EUR') {
  const fallback = { value: String(price ?? ''), currency: '€', currencyFirst: false };
  if (price === null || price === undefined || Number.isNaN(Number(price))) {
    return { value: '', currency: '', currencyFirst: false };
  }
  const n = Number(price);
  try {
    const parts = new Intl.NumberFormat(getLocale(lang), {
      style: 'currency',
      currency: (currency || 'EUR').toUpperCase(),
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).formatToParts(n);

    const currencyIndex = parts.findIndex(p => p.type === 'currency');
    const symbol = currencyIndex >= 0 ? parts[currencyIndex].value : '€';
    // Tout sauf le symbole et l'espace insécable qui l'accompagne.
    const value = parts
      .filter((p, i) => p.type !== 'currency' && !(p.type === 'literal' && Math.abs(i - currencyIndex) === 1))
      .map(p => p.value)
      .join('')
      .trim();

    return {
      value,
      currency: symbol,
      currencyFirst: currencyIndex === 0,
    };
  } catch {
    return fallback;
  }
}

/**
 * Formate un montant Stripe (centimes) en devise locale.
 *   formatStripeAmount(8900, 'eur', 'fr') → "89,00 €"
 *   formatStripeAmount(8900, 'eur', 'en') → "€89.00"
 */
export function formatStripeAmount(amountCents, currency, lang) {
  if (typeof amountCents !== 'number' || !currency) return '';
  return formatCurrency(amountCents / 100, lang, currency);
}
